/**
 * SettingsPage — the one Settings area, `/settings/:section`, with a left section list.
 *
 * Phase 9 / Plan 09-04 — stub placeholder.
 * Phase 11 / Task 11.1 (DEC-120) — adds the InReview Settings panel, visible to
 * TENANT_ADMIN (and PLATFORM_ADMIN) only.
 *
 * Quick 260827-pwd — this page used to render the literal string "Coming soon" to
 * every non-admin role, which meant a dispatcher had no settings surface at all and
 * nowhere to change their own password. Every signed-in user now gets a real page;
 * the admin-only panels are additive on top of it rather than the whole page being
 * gated behind a role check.
 *
 * Quick 261013-srg PR 1 — Settings and Setup & Configuration are merged into this one area.
 * Every panel and editor is moved in UNCHANGED (same components, same save behaviour, same role
 * gates); `/setup-config` redirects to Getting started. Sections and their visibility live in
 * features/settings/sections.ts. Later PRs regroup the panels, fix the role gates and add the
 * save bar.
 *
 * Quick 261013-srg PR 2 — a section, and every editor inside one, shows only to roles whose API
 * calls will succeed (no manager 403s). Dispatch managers VIEW the review rules and the
 * completion policy read-only (Corbin's decision 3).
 *
 * Spec: UI-SPEC Surface Inventory §1 (Settings nav section).
 */

import * as React from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ChangePassword } from '@lodex-pro/web-shared';
import { useUserProfile } from '../lib/use-user-timezone';
import { confirmNavigation } from '../lib/navigation-guard';
import {
  MANAGE_ROLES,
  MANAGE_SERVICE_CHARGES_ROLES,
  SETTINGS_ADMIN_ROLES,
} from '../lib/role-gates';
import { InReviewSettingsPanel } from '../features/review/InReviewSettingsPanel';
import { CompletionPolicyPanel } from '../features/stuck-jobs/CompletionPolicyPanel';
import { CompanyLogoPanel } from '../features/company-logo/CompanyLogoPanel';
import { PrintSettingsPanel } from '../features/print-settings/PrintSettingsPanel';
import { LoadEmailSettingsSection } from '../features/load-emails/LoadEmailSettingsSection';
import { DisposalDisclosureSection } from '../features/disposal-disclosure/DisposalDisclosureSection';
import { ProductsEditor } from '../features/products/ProductsEditor';
import { ExtraServicesEditor } from '../features/extra-services/ExtraServicesEditor';
import { AccountingExportEditor } from '../features/accounting-export/AccountingExportEditor';
import { BillDatePanel } from '../features/billing-date/BillDatePanel';
import { FuelCostEditor } from '../features/fuel-cost/FuelCostEditor';
import { DriverCostEditor } from '../features/fuel-cost/DriverCostEditor';
import { OnTimeRuleEditor } from '../features/on-time/OnTimeRuleEditor';
import { TankEstimatesEditor } from '../features/tanks/TankEstimatesEditor';
import { ShiftIdleCloseEditor } from '../features/shift-settings/ShiftIdleCloseEditor';
import { AutoArrivalEditor } from '../features/shift-settings/AutoArrivalEditor';
import { TeamEditor } from '../features/team/TeamEditor';
import { LinkRequestsPanel } from '../features/team/LinkRequestsPanel';
import { SiteTypesEditor } from '../features/site-types/SiteTypesEditor';
// Operator portal rework PR 9 — read-only list of the operator API keys on this company's data.
import { ApiConnectionsSection } from '../features/api-connections/ApiConnectionsSection';
import { DriverEntryRulesSection } from '../features/validation-rules/DriverEntryRulesSection';
import type { StepProps } from '../features/setup-wizard/steps';
import { GettingStartedSection } from '../features/settings/GettingStartedSection';
import {
  defaultSettingsSection,
  visibleSettingsSections,
  type SettingsSectionId,
} from '../features/settings/sections';

/**
 * Account section — available to EVERY signed-in role.
 *
 * `updatePassword` requires the current password and the caller already holds a
 * valid session, so this grants no capability the user does not already have. See
 * the note in web-shared/auth/change-password.tsx for why admins are included too.
 */
function AccountSection(props: { readonly email: string | null }): React.JSX.Element {
  return (
    <section
      aria-labelledby="settings-account-heading"
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        borderRadius: 'var(--radius-card)',
        padding: '20px',
      }}
    >
      <h2
        id="settings-account-heading"
        style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}
      >
        Account
      </h2>
      <p
        style={{
          fontSize: '13px',
          color: 'var(--color-text-secondary)',
          marginBottom: '16px',
        }}
      >
        {props.email !== null
          ? `Signed in as ${props.email}.`
          : 'Change the password you use to sign in.'}
      </p>
      <div className="auth-card-inline">
        <ChangePassword />
      </div>
    </section>
  );
}

/**
 * Data import entry point — Phase 20 / Task 20.7 (§80 "Settings → Import").
 * TENANT_ADMIN + DISPATCH_MANAGER (+ PLATFORM_ADMIN) per DEC-256, which drops
 * §80's Dispatcher.
 */
function ImportSection(): React.JSX.Element {
  const navigate = useNavigate();
  return (
    <section
      aria-labelledby="settings-import-heading"
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        borderRadius: 'var(--radius-card)',
        padding: '20px',
      }}
    >
      <h2
        id="settings-import-heading"
        style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}
      >
        Import data
      </h2>
      <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginBottom: '16px' }}>
        Load customers, sites, tanks, drivers, trucks and more from an Excel or CSV file.
      </p>
      <button
        type="button"
        onClick={(): void => {
          void navigate('/settings/import');
        }}
        style={{
          minHeight: '56px',
          padding: '0 20px',
          borderRadius: 'var(--radius-pill)',
          fontSize: '16px',
          fontWeight: 600,
          cursor: 'pointer',
          backgroundColor: 'var(--color-accent-blue)',
          color: 'var(--color-on-accent)',
          border: '1px solid var(--color-accent-blue)',
        }}
      >
        Open import
      </button>
    </section>
  );
}

/** One editor moved over from Setup & Configuration: its old heading and sentence, then the editor. */
function EditorBlock(props: {
  readonly title?: string;
  readonly description?: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section>
      {props.title !== undefined && (
        <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>{props.title}</h2>
      )}
      {props.description !== undefined && (
        <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '16px' }}>
          {props.description}
        </p>
      )}
      {props.children}
    </section>
  );
}

function SectionBody(props: {
  readonly id: SettingsSectionId;
  readonly role: string;
  readonly email: string | null;
}): React.JSX.Element {
  const { id, role, email } = props;
  // MANAGE_TENANT holders: the API's gate on every company-wide setting below.
  const isAdmin = SETTINGS_ADMIN_ROLES.has(role);
  const stepProps: StepProps = { canManage: MANAGE_ROLES.has(role), isTenantAdmin: isAdmin };
  // OPEN-111 — Extra Services is gated on the API's own permission, MANAGE_SERVICE_CHARGES
  // (TENANT_ADMIN only), not on isAdmin: a PLATFORM_ADMIN would see it and get a 403 on save.
  const canManageServiceCharges = MANAGE_SERVICE_CHARGES_ROLES.has(role);

  switch (id) {
    case 'getting-started':
      return <GettingStartedSection stepProps={stepProps} />;
    case 'account':
      return <AccountSection email={email} />;
    case 'review':
      // Decision 3: managers see both read-only; only MANAGE_TENANT holders can change them.
      return (
        <>
          <InReviewSettingsPanel canEdit={isAdmin} />
          <CompletionPolicyPanel canEdit={isAdmin} />
        </>
      );
    case 'driver-rules':
      // OPEN-217 (DEC-382) — the section's gate is the API's own (MANAGE_VALIDATION_RULES).
      return <DriverEntryRulesSection />;
    case 'drivers-shifts':
      // OPEN-110 — idle driver shifts are closed automatically after this many hours.
      return (
        <>
          <EditorBlock title="Idle shift auto-close">
            <ShiftIdleCloseEditor />
          </EditorBlock>
          {/* OPEN-220a / DEC-386 — may the phone mark arrival on its own (OFF by default). */}
          <EditorBlock title="Arrivals">
            <AutoArrivalEditor />
          </EditorBlock>
        </>
      );
    case 'jobs-pricing':
      return (
        <>
          {/* DEC-175 — Products is a catalog section, not a numbered wizard step. */}
          <EditorBlock
            title="Products"
            description="The products your drivers capture per tank (e.g. Produced Water, Fresh Water). Changes save immediately."
          >
            <ProductsEditor />
          </EditorBlock>
          {canManageServiceCharges && (
            <EditorBlock
              title="Extra Services"
              description="Extra charges drivers add to a ticket, and which screen they're entered on. Changes save immediately."
            >
              <ExtraServicesEditor />
            </EditorBlock>
          )}
          {/* DEC-326 item 3a — company fuel cost per mile; Phase 28 (DEC-331 D-4) — driver
              time, for disposal suggestions. Kept together. */}
          {/* fuel-settings and driver-cost-settings are MANAGE_TENANT. */}
          {isAdmin && (
            <EditorBlock title="Running costs">
              <FuelCostEditor />
              <div style={{ height: '24px' }} />
              <DriverCostEditor />
            </EditorBlock>
          )}
          {/* site-types/settings is MANAGE_TENANT. */}
          {isAdmin && (
            <EditorBlock
              title="Site types"
              description="Which kinds of site your dispatchers can choose from when creating one. Paelode owns the catalog itself; this only controls what your company sees. Hiding a type never affects sites you already have."
            >
              <SiteTypesEditor />
            </EditorBlock>
          )}
        </>
      );
    case 'company-logo':
      // Task #58 — MANAGE_TENANT on the API.
      return <CompanyLogoPanel />;
    case 'printed-tickets':
      return <PrintSettingsPanel />;
    case 'customer-emails':
      // Phase 17 / Task 17.3 — MANAGE_TENANT on the API.
      return <LoadEmailSettingsSection />;
    case 'disposal-sharing':
      // Phase 16 / Task 16.8 (DEC-007) — MANAGE_TENANT on the API.
      return <DisposalDisclosureSection />;
    case 'reporting':
      // DEC-326 item 5 — how strict "on time" is for this company.
      // Phase 13 / 13.1c (DEC-351) — estimated tank levels on or off. Same MANAGE_TENANT gate.
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          <EditorBlock title="On-time rule">
            <OnTimeRuleEditor />
          </EditorBlock>
          <EditorBlock title="Tank level estimates">
            <TankEstimatesEditor />
          </EditorBlock>
        </div>
      );
    case 'accounting':
      // Hauler billing gap 6 (0250) — the day each ticket is billed on, then ClickUp
      // 86baxrd1j — QBD accounting export.
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          <BillDatePanel canEdit={isAdmin} />
          <EditorBlock description="How invoices are exported for QuickBooks Desktop via Transaction Pro Importer. Changes save immediately.">
            <AccountingExportEditor />
          </EditorBlock>
        </div>
      );
    case 'team':
      // Quick 260814-ud3 — Team.
      return (
        <EditorBlock description="Dispatchers and dispatch managers for your company. Drivers are managed in Fleet → Drivers, which holds their shift, yard, CDL and phone.">
          <TeamEditor />
          {/* DEC-346 (a): people an import asked to join, answered in their own app. */}
          <LinkRequestsPanel />
        </EditorBlock>
      );
    case 'api-connections':
      // Operator portal rework PR 9 — read only: Paelode and the operator manage the keys.
      return <ApiConnectionsSection />;
    case 'import-data':
      return <ImportSection />;
  }
}

export function SettingsPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { section } = useParams<{ section?: string }>();
  const { data: profileData, isPending } = useUserProfile();
  const role = profileData?.profile.role ?? '';
  const email = profileData?.profile.email ?? null;

  if (isPending) {
    return (
      <div role="status" style={{ padding: '24px' }}>
        Loading…
      </div>
    );
  }

  const sections = visibleSettingsSections(role);
  const active = sections.find((s) => s.id === section);
  // `/settings`, an unknown section, or one this role cannot use: go to the role's landing
  // section. Replace, so Back does not bounce through the bad URL.
  if (active === undefined) {
    return <Navigate to={`/settings/${defaultSettingsSection(role)}`} replace />;
  }

  return (
    <div
      style={{
        display: 'flex',
        height: '100%',
        backgroundColor: 'var(--color-bg-primary)',
        fontFamily: 'var(--font-sans, system-ui, sans-serif)',
      }}
    >
      <nav
        aria-label="Settings sections"
        style={{
          flex: '0 0 240px',
          overflowY: 'auto',
          padding: '16px 12px',
          borderRight: '1px solid var(--color-border-primary)',
          backgroundColor: 'var(--color-bg-secondary)',
        }}
      >
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {sections.map((s) => {
            const current = s.id === active.id;
            return (
              <li key={s.id}>
                <button
                  type="button"
                  aria-current={current ? 'page' : undefined}
                  onClick={(): void => {
                    if (current || !confirmNavigation()) return;
                    void navigate(`/settings/${s.id}`);
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    minHeight: '44px',
                    padding: '0 12px',
                    textAlign: 'left',
                    fontSize: '14px',
                    fontWeight: current ? 600 : 400,
                    color: current ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                    backgroundColor: current ? 'var(--color-bg-tertiary)' : 'transparent',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                  }}
                >
                  {s.label}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
      <div style={{ flex: '1 1 auto', overflow: 'auto', padding: '24px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '900px' }}>
          <h1 style={{ fontSize: '20px', fontWeight: 600 }}>{active.label}</h1>
          <SectionBody id={active.id} role={role} email={email} />
        </div>
      </div>
    </div>
  );
}
